import { StatusBadge, rechenstand, type Fachstatus, type Vorbehalt } from '../../muster';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * KOPF DER SANITÄR- UND AUSSENBERECHNUNG (Paket TS9a, TX-STANDARD S1.2)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Rechenstand in der Pflichtform („Lauf ‹Kurz-ID› (‹Norm›) · gerechnet ‹…› ·
 * Modellstand ‹…›“, ohne Lauf „noch nicht gerechnet“) mit der Marke aus
 * `fachstatus.rechenstand` - und die Vorbehalte aus offenen Fachfragen, mit
 * demselben Satz wie die Excel-Mappe (`Berechnungslisten.San05`/`Aua22`):
 * Seite und Liste sagen dasselbe.
 */

const ZEIT = new Intl.DateTimeFormat('de-DE', {
  day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Berlin',
});
const TAG = new Intl.DateTimeFormat('de-DE', {
  day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Europe/Berlin',
});

/**
 * Ein Zeitpunkt des Servers. Die Läufe kommen als UTC ohne Zonenangabe („2026-10-08T16:20:00“, Pomelo liest
 * `DateTime` ohne Art); `new Date` läse das als Ortszeit und läge um eine bis zwei Stunden daneben. Ohne Angabe
 * gilt deshalb UTC - wie im Kopf der Excel-Mappe („16:20 UTC“).
 */
export function alsUtc(iso: string): Date {
  return new Date(/(?:Z|[+-]\d\d:?\d\d)$/i.test(iso) || !iso.includes('T') ? iso : `${iso}Z`);
}

/** „08.10.2026, 18:20“ (Ortszeit Deutschland); leer bei einer Fehlstelle. */
export function zeitpunkt(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = alsUtc(iso);
  return Number.isNaN(d.getTime()) ? '' : ZEIT.format(d);
}

/** „12.10.2026“; leer bei einer Fehlstelle. */
export function tag(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = alsUtc(iso);
  return Number.isNaN(d.getTime()) ? '' : TAG.format(d);
}

export interface Laufangabe {
  id: string;
  norm?: string | null;
  gerechnetAm: string;
  modellstand?: string | null;
}

export function rechenstandText(lauf: Laufangabe | null | undefined): string {
  if (!lauf) return 'noch nicht gerechnet';
  const teile = [`Lauf ${lauf.id.replace(/-/g, '').slice(0, 8)}${lauf.norm ? ` (${lauf.norm})` : ''}`,
    `gerechnet ${zeitpunkt(lauf.gerechnetAm)}`];
  if (lauf.modellstand) teile.push(`Modellstand ${tag(lauf.modellstand)}`);
  return teile.join(' · ');
}

/**
 * Der Rechenstand als Text mit Marke (aktuell / veraltet / nicht gerechnet). `veraltet` ist das Urteil des Servers
 * (Sanitär `standVeraltet`, Außen `standVeraltet`) und geht vor: Die Zeitpunkte der Läufe kommen ohne Zonenangabe,
 * ein Vergleich im Browser läge um die Ortszeit daneben.
 */
export function Rechenstand({ lauf, veraltet }: { lauf: Laufangabe | null | undefined; veraltet?: boolean }) {
  const s: Fachstatus = lauf && veraltet !== undefined
    ? (veraltet ? { zustand: 'warnung', text: 'veraltet' } : { zustand: 'gut', text: 'aktuell' })
    : rechenstand(lauf ? alsUtc(lauf.gerechnetAm) : null, lauf?.modellstand ? alsUtc(lauf.modellstand) : null);
  return (
    <span data-rechenstand>
      {rechenstandText(lauf)}{' '}
      <StatusBadge {...s} klein />
    </span>
  );
}

/** Derselbe Satz wie `Berechnungslisten.San05` (Excel). */
export const VORBEHALT_SAN05 = 'Vorbehalt SAN-05: Der Trinkwasser-Hausanschluss ist nicht bestätigt. Die Netzteile haben keine '
  + 'bestätigte Quelle, die Teilstrecken stehen am 2-m/s-Deckel - keine gültige Bemessung nach DIN 1988-300. Die Liste zeigt '
  + 'den Lauf, wie Seite und PDF ihn zeigen.';

/** Derselbe Satz wie `Berechnungslisten.Aua22` (Excel). */
export const VORBEHALT_AUA22 = 'Vorbehalt AUA-22: Die Übergänge Gebäude → Außen sind nicht bestätigt. Kein Übergang ist Wurzel '
  + 'des Gebäudenetzes, die Übergangsrohre führen dort keinen Abfluss, und die Außenrechnung setzt den ganzen Kreis als '
  + 'obere Schranke an.';

/** Derselbe Satz wie `Berechnungslisten.Aua08` (Excel, TS9b2): Überflutungsnachweis. */
export const VORBEHALT_AUA08 = 'Vorbehalt AUA-08/SAN-33: Jährlichkeit (30 a nach Norm oder 100 a nach FLB), Flächenaufteilung, '
  + 'schadlos überflutbare Flächen und Geländeoberkante sind nicht bestätigt, ebenso die Regenspende der Dachentwässerung '
  + '(r(5,5) oder r(5,100)). Der Nachweis rechnet mit ungeprüften Annahmen - kein Nachweis nach DIN 1986-100.';

/** Derselbe Satz wie `Berechnungslisten.Aua0102` (Excel, TS9b2): Rückhaltung im Versickerungslauf. */
export const VORBEHALT_AUA0102 = 'Vorbehalt AUA-01/02: Die 50 m³ am Pumpwerk gelten als Schmutzwasser-Rückhaltebecken (nicht '
  + 'bestätigt), und für die Regenwasser-Einleitstellen sind weder Erlaubnis noch Drosselabfluss bekannt. Die Rückhaltung '
  + 'nach DWA-A 117 ist ohne Drossel nicht bestimmbar; Volumen stehen nur als obere Schranke.';

/** Derselbe Satz wie `Berechnungslisten.Aua0405` (Excel, TS9b2): Löschwasser. */
export const VORBEHALT_AUA0405 = 'Vorbehalt AUA-04/05: Zwischenbehälter und Nachspeisung der Hydranten-Trennstation (8 h als '
  + 'Annahme) sowie Lage, Kennlinie und Einspeisepunkt der Trennstation am Ring DN 150 sind nicht bestätigt. Die '
  + 'Druckbilanz nimmt die Einspeisung im Netzschwerpunkt an.';

/** Welcher Sanitärbereich unter welchem Vorbehalt steht - wie `Berechnungslisten.VorbehaltVon`. */
export function sanitaerFachvorbehalt(bereich: string): Vorbehalt | null {
  if (bereich === 'trinkwasser' || bereich === 'warmwasser') return { art: 'fachfrage', text: VORBEHALT_SAN05 };
  if (bereich === 'schmutzwasser') return { art: 'fachfrage', text: VORBEHALT_AUA22 };
  if (bereich === 'ueberflutung') return { art: 'fachfrage', text: VORBEHALT_AUA08 };
  return null;
}

/** Welcher Außenbereich unter welchem Vorbehalt steht - wie `Berechnungslisten.VorbehaltVon`. */
export function aussenFachvorbehalt(bereich: string): Vorbehalt | null {
  if (bereich === 'trinkwasser') return { art: 'fachfrage', text: VORBEHALT_SAN05 };
  if (bereich === 'uebergang' || bereich === 'freispiegel') return { art: 'fachfrage', text: VORBEHALT_AUA22 };
  if (bereich === 'versickerung') return { art: 'fachfrage', text: VORBEHALT_AUA0102 };
  if (bereich === 'loeschwasser') return { art: 'fachfrage', text: VORBEHALT_AUA0405 };
  return null;
}

